import { refreshSessionOnce, request, requestNoContent, setAccessToken } from './client';

/** Phản hồi của một lần đăng nhập hoặc làm mới phiên thành công. */
export interface SessionResponse {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly userId: string;
  readonly userName: string;
  readonly fullName: string;
  readonly companyId: string;
  readonly mustChangePassword: boolean;
}

/** Thông tin người dùng của phiên hiện tại. */
export interface CurrentUserResponse {
  readonly userId: string | null;
  readonly userName: string | null;
  readonly companyId: string | null;
  readonly businessDate: string;
}

/**
 * Đăng nhập.
 *
 * Refresh token không xuất hiện trong kết quả: máy chủ gửi nó bằng cookie mà JavaScript
 * không đọc được. Giao diện chỉ giữ phiếu truy cập, và giữ trong bộ nhớ.
 */
export async function signIn(userName: string, password: string): Promise<SessionResponse> {
  const session = await request<SessionResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: { userName, password },
    skipRefresh: true,
  });

  setAccessToken(session.accessToken);

  return session;
}

/**
 * Thử phục hồi phiên từ cookie.
 *
 * Gọi một lần khi ứng dụng khởi động. Người dùng đã đăng nhập ở lần trước sẽ vào thẳng
 * hệ thống mà không thấy trang đăng nhập, vì cookie vẫn còn dù phiếu truy cập đã mất
 * theo lần tải lại trang.
 */
export async function restoreSession(): Promise<SessionResponse | null> {
  /*
    Đi qua cơ chế chống gọi trùng của tầng gọi API, KHÔNG gọi thẳng điểm cuối.

    Hàm này chạy trong một hiệu ứng của React, và ở chế độ phát triển React gắn hiệu ứng hai lần có
    chủ ý. Gọi thẳng thì lời gọi thứ hai gửi lên refresh token vừa bị lời gọi thứ nhất dùng mất, máy
    chủ coi đó là token bị đánh cắp và thu hồi cả họ token — người dùng bị đăng xuất ở mỗi lần tải
    trang. Cùng chuyện đó xảy ra khi người dùng mở hai thẻ cùng lúc.

    Trả về rỗng khi không có cookie hoặc phiên đã hết hiệu lực. Cả hai đều nghĩa là phải đăng nhập
    lại, và đó là trạng thái bình thường ở lần vào đầu tiên.
  */
  return await refreshSessionOnce();
}

/**
 * Đăng xuất.
 *
 * Luôn xóa phiếu trong bộ nhớ, kể cả khi lời gọi tới máy chủ thất bại. Nếu chỉ xóa khi máy chủ
 * trả về thành công thì một sự cố mạng sẽ khiến người dùng tưởng mình đã đăng xuất mà thực ra
 * vẫn còn phiên trên máy — đúng tình huống tệ nhất khi họ đang dùng máy dùng chung.
 */
export async function signOut(): Promise<void> {
  try {
    await requestNoContent('/api/v1/auth/logout', { method: 'POST', skipRefresh: true });
  } finally {
    setAccessToken(null);
  }
}

/**
 * Đổi mật khẩu của chính mình.
 *
 * Máy chủ trả về một phiên MỚI, và giao diện phải dùng nó. Lý do: việc đổi mật khẩu làm mọi phiếu
 * đang lưu hành mất hiệu lực, kể cả phiếu vừa dùng để gọi chính lời gọi này. Không nhận phiên mới
 * thì lời gọi tiếp theo bị từ chối và người dùng bị đẩy ra trang đăng nhập ngay sau khi đổi xong.
 *
 * Định danh người dùng không nằm trong nội dung gửi lên — máy chủ lấy nó từ phiếu xác thực.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<SessionResponse> {
  const session = await request<SessionResponse>('/api/v1/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });

  setAccessToken(session.accessToken);

  return session;
}

/** Lấy thông tin người dùng của phiên hiện tại. */
export async function getCurrentUser(): Promise<CurrentUserResponse> {
  return await request<CurrentUserResponse>('/api/v1/auth/me');
}
